import React, { useState, useEffect } from 'react';
import { homepageAPI } from '../lib/api';
import { Layout } from '../components/layout/Layout';
import { HeadlineStory } from '../components/home/HeadlineStory';
import { TrendingVideos } from '../components/home/TrendingVideos';
import { CategoryRail } from '../components/home/CategoryRail';
import { PopularSidebar } from '../components/home/PopularSidebar';
import { ArticleCard } from '../components/articles/ArticleCard';
import { Skeleton } from '../components/ui/skeleton';

const CATEGORY_IMAGES = {
  film: 'https://images.unsplash.com/photo-1579912437184-595291124a34?w=600&h=400&fit=crop',
  music: 'https://images.unsplash.com/photo-1648260029310-5f1da359af9d?w=600&h=400&fit=crop',
  tv: 'https://images.unsplash.com/photo-1573717626258-e9d6879998f0?w=600&h=400&fit=crop',
  sports: 'https://images.unsplash.com/photo-1759694468344-b5fffb9e6341?w=600&h=400&fit=crop',
  business: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&fit=crop',
};

// Define category order
const CATEGORY_ORDER = ['film', 'music', 'tv', 'sports', 'business'];

export const HomePage = () => {
  const [headlineArticle, setHeadlineArticle] = useState(null);
  const [trendingVideos, setTrendingVideos] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [popularArticles, setPopularArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [articlesByCategory, setArticlesByCategory] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Single optimized API call for all homepage data
        const res = await homepageAPI.getData();
        const data = res.data;
        
        setHeadlineArticle(data.headline);
        setTrendingVideos(data.trending_videos || []);
        setFeaturedArticles(data.featured_articles || []);
        setPopularArticles(data.popular_articles || []);
        
        // Sort categories by defined order
        const sortedCats = (data.categories || []).sort((a, b) => {
          const aIndex = CATEGORY_ORDER.indexOf(a.slug);
          const bIndex = CATEGORY_ORDER.indexOf(b.slug);
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
        setCategories(sortedCats);
        
        // Add fallback images to category articles
        const categoryArticles = {};
        for (const slug of CATEGORY_ORDER) {
          const articles = data.articles_by_category?.[slug] || [];
          categoryArticles[slug] = articles.map(article => ({
            ...article,
            hero_image: article.hero_image || CATEGORY_IMAGES[slug]
          }));
        }
        setArticlesByCategory(categoryArticles);

      } catch (error) {
        console.error('Error fetching homepage data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Create placeholder articles if no data
  const createPlaceholderArticles = (categorySlug, count = 4) => {
    return Array.from({ length: count }, (_, i) => ({
      id: `placeholder-${categorySlug}-${i}`,
      slug: `placeholder-${categorySlug}-${i}`,
      title: `Latest ${categorySlug.charAt(0).toUpperCase() + categorySlug.slice(1)} Story ${i + 1}`,
      author: 'The Trade Media',
      created_at: new Date().toISOString(),
      hero_image: CATEGORY_IMAGES[categorySlug],
      category: { name: categorySlug.charAt(0).toUpperCase() + categorySlug.slice(1), slug: categorySlug }
    }));
  };

  // If no headline, use first featured article as fallback
  const displayHeadline = headlineArticle || (featuredArticles.length > 0 ? featuredArticles[0] : null);

  // Get sorted categories for display
  const displayCategories = categories.length > 0 
    ? categories 
    : CATEGORY_ORDER.map(slug => ({ 
        name: slug.charAt(0).toUpperCase() + slug.slice(1), 
        slug 
      }));

  return (
    <Layout>
      {/* 1. Static Headline Story (NO SCROLLING) */}
      <HeadlineStory article={displayHeadline} loading={loading} />

      {/* 2. Latest Stories + Popular Sidebar */}
      <div className="py-8 md:py-12">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Main Column */}
            <div className="lg:col-span-2">
              <section data-testid="latest-stories">
                <h2 className="font-serif text-2xl md:text-3xl font-bold mb-6 pb-4 border-b border-border">
                  Latest Stories
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {loading ? (
                    // Loading skeletons
                    Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="space-y-3">
                        <Skeleton className="aspect-[16/10] w-full" />
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-6 w-full" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                    ))
                  ) : (
                    (featuredArticles.length > 0 
                      ? featuredArticles
                          .filter(a => !displayHeadline || a.id !== displayHeadline.id)
                          .slice(0, 4) 
                      : createPlaceholderArticles('film', 4)
                    ).map((article) => (
                      <ArticleCard key={article.id} article={article} />
                    ))
                  )}
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <PopularSidebar 
                articles={popularArticles.length ? popularArticles : createPlaceholderArticles('music', 5)}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 3. Trending Videos (MOVED BELOW LATEST STORIES) */}
      <TrendingVideos videos={trendingVideos} />

      {/* 4. Category Sections - Film, Music, TV, Sports, Business */}
      {displayCategories.map((category) => {
        const categoryArticles = articlesByCategory[category.slug];
        const hasArticles = categoryArticles && categoryArticles.length > 0;
        return (
          <CategoryRail
            key={category.slug}
            title={category.name}
            slug={category.slug}
            articles={hasArticles ? categoryArticles : createPlaceholderArticles(category.slug, 6)}
            className="border-t border-border"
          />
        );
      })}
    </Layout>
  );
};

export default HomePage;
